import { Calendar, Briefcase, Users } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const HowWeWork = () => {
  const engagementModels = [{
    title: "Project-Based",
    icon: Briefcase,
    description: "Targeted support for specific product challenges. Ideal for early-stage teams needing quick wins or clear deliverables.",
    examples: ["Defining your ideal customer persona and journey", "Auditing or improving customer experience flows", "Prioritizing MVP features"],
    timeline: "2–6 weeks",
    deliverables: "Research, flows, strategy docs, workshop outputs"
  }, {
    title: "Short-Term Contracts",
    icon: Calendar,
    description: "Deeper engagements for strategic shifts. Perfect for companies refining direction, launching new features, or entering new markets.",
    examples: ["Crafting a refined product strategy", "Designing and operationalizing a go-to-market plan", "Evaluating tools/platforms for upcoming builds"],
    timeline: "1–3 months",
    deliverables: "Strategy frameworks, GTM plans, stakeholder briefs, roadmaps"
  }, {
    title: "Longer-Term Support",
    icon: Users,
    description: "Fractional product leadership or team augmentation. I embed alongside your team to move from vision to build—without the risk of a full-time hire.",
    examples: ["Defining feature requirements and writing user stories", "Partnering with engineering and design teams", "Collecting and incorporating user feedback into roadmap"],
    timeline: "3+ months",
    deliverables: "Product specs, sprint planning, iteration cycles, feedback loops"
  }];
  return <section id="how-we-work" className="section bg-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-darkText">
            How I <span className="text-cyanPrimary">Work</span>
          </h2>
          <p className="text-lg text-darkText/80 leading-relaxed">
            I offer flexible engagement models tailored to your specific needs. Whether you're defining your first MVP or scaling thoughtfully, I plug in exactly where you need support most.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {engagementModels.map((model, index) => <Card key={index} className="bg-grayLight border-0 hover:shadow-lg transition-shadow rounded-2xl overflow-hidden flex flex-col">
              <CardHeader className="text-center pb-2">
                <div className="w-12 h-12 bg-cyan-400 rounded-full flex items-center justify-center mx-auto mb-4">
                  <model.icon className="text-white" size={24} />
                </div>
                <CardTitle className="text-xl mb-2 text-darkText">{model.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col h-full">
                {/* Description container with fixed height */}
                <div className="min-h-[120px] flex items-start mb-6">
                  <p className="text-darkText/80">{model.description}</p>
                </div>
                
                {/* Examples section - now aligned */}
                <div className="mb-6">
                  <h4 className="font-medium mb-3 text-darkText">Examples:</h4>
                  <ul className="space-y-2">
                    {model.examples.map((example, i) => <li key={i} className="flex items-start">
                        <span className="min-w-[10px] w-2.5 h-2.5 bg-cyanPrimary rounded-full mr-2 mt-1.5 flex-shrink-0" style={{
                    aspectRatio: '1/1'
                  }}></span>
                        <span className="text-darkText/80 text-left">{example}</span>
                      </li>)}
                  </ul>
                </div>

                {/* Spacer to push timeline to bottom */}
                <div className="flex-grow"></div>
                
                {/* Timeline section */}
                <div className="pt-4 border-t border-grayMedium/30">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-darkText">Timeline:</span>
                    <span className="text-darkText/80">{model.timeline}</span>
                  </div>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-cyanPrimary flex-shrink-0">•</span>
                    <div className="text-left">
                      <span className="font-medium text-darkText mr-1">Sample Deliverables:</span>
                      <span className="text-darkText/80">{model.deliverables}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
};
export default HowWeWork;